/**
 * Date and time formatting, in the language the interface is showing.
 *
 * `toLocaleDateString()` with no locale argument uses the *browser's* locale,
 * not the app's. On a Brazilian machine reading the portal in English that
 * renders "13 de ago. de 2026" in the middle of an English screen — which is
 * what seventeen call sites across admin, moderator and the student area were
 * doing. The three that got it right passed `i18n.language` by hand.
 *
 * These helpers read the live i18next language, so switching the language in
 * the header re-renders dates along with the copy.
 *
 * Invalid or missing input returns an em dash rather than "Invalid Date": these
 * are timestamps from the API, and a half-written record should read as absent,
 * not as an error string in the middle of a table.
 */

import i18n from 'i18next';

const EMPTY = '—';

/** Accepts an ISO string, a Date, or epoch *seconds* (the API's UTC columns). */
type DateInput = string | number | Date | null | undefined;

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  // Epoch seconds, not milliseconds — every numeric timestamp in this API is
  // `*_utc` in seconds, and the call sites all multiplied by 1000 by hand.
  const d = typeof value === 'number' ? new Date(value * 1000) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function format(value: DateInput, options: Intl.DateTimeFormatOptions): string {
  const d = toDate(value);
  if (!d) return EMPTY;
  try {
    return d.toLocaleString(i18n.language, options);
  } catch {
    return EMPTY;
  }
}

/** 13 Aug 2026 */
export function formatDate(value: DateInput): string {
  return format(value, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** 13 Aug 2026, 14:41 */
export function formatDateTime(value: DateInput): string {
  return format(value, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** 14:41 */
export function formatTime(value: DateInput): string {
  return format(value, { hour: '2-digit', minute: '2-digit' });
}

/**
 * Same as formatDateTime, pinned to UTC and labelled as such.
 *
 * Moderator screens compare timestamps across players in different time zones,
 * so a local rendering there is actively misleading.
 */
export function formatDateTimeUTC(value: DateInput): string {
  return format(value, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  });
}

/**
 * A count of seconds as minutes and two-digit seconds — "12:34" — however many
 * minutes: how long a match has run, and where a moment falls in a recorded
 * session.
 */
export function formatMinutesSeconds(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
